import * as React from 'react'
import type { DaySettings, WeekDay, TimeSlot } from '@/types/booking'
import { Button } from '@/components/ui/button'
import { PlusCircle, X, } from 'lucide-react'
import { CopyDaysPopup } from './CopyDaysPopup'
import { TimePickerCombobox } from '@/components/ui/TimePickerCombobox'

interface AvailabilityDayRowProps {
  day: WeekDay
  dayLabel: string
  settings: DaySettings
  onToggleDay: (day: WeekDay) => void
  onAddTimeSlot: (day: WeekDay) => void
  onRemoveTimeSlot: (day: WeekDay, index: number) => void
  onCopyDay: (sourceDay: WeekDay, targetDays: WeekDay[]) => void
  onUpdateTimeSlot: (day: WeekDay, index: number, timeSlot: TimeSlot) => void
}

const DAYS = [
  { day: 'monday' as WeekDay, label: 'Monday' },
  { day: 'tuesday' as WeekDay, label: 'Tuesday' },
  { day: 'wednesday' as WeekDay, label: 'Wednesday' },
  { day: 'thursday' as WeekDay, label: 'Thursday' },
  { day: 'friday' as WeekDay, label: 'Friday' },
  { day: 'saturday' as WeekDay, label: 'Saturday' },
  { day: 'sunday' as WeekDay, label: 'Sunday' },
]

export function AvailabilityDayRow({
  day,
  dayLabel,
  settings,
  onToggleDay,
  onAddTimeSlot,
  onRemoveTimeSlot,
  onCopyDay,
  onUpdateTimeSlot,
}: AvailabilityDayRowProps) {
  const [isCopyPopupOpen, setIsCopyPopupOpen] = React.useState(false)
  return (
    <>
      <div className="py-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-medium">
                {dayLabel[0].toUpperCase()}
              </span>
            </div>
            <span className="font-medium">{dayLabel}</span>
          </div>
          {!settings.timeSlots.length && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onToggleDay(day)}
            >
              <PlusCircle className="h-4 w-4" />
            </Button>
          )}
        </div>

        {settings.timeSlots.length > 0 && (
          <div className="space-y-2">
            {settings.timeSlots.map((slot, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex-1 flex items-center gap-2">
                  <TimePickerCombobox
                    value={slot.start}
                    onChange={(value) => {
                      let newEnd = slot.end;
                      // If the new start time is after the current end time or within 2 hours of it,
                      // automatically adjust the end time to be 2 hours later (capped at 8 hours and 24:00)
                      if (value >= slot.end || (slot.end - value) < 120) {
                        newEnd = Math.min(value + 120, 1440);
                      }
                      onUpdateTimeSlot(day, index, { start: value, end: newEnd });
                    }}
                    otherTimeSlots={settings.timeSlots.filter((_, i) => i !== index)}
                    isStartTime={true}
                  />
                  <span className="text-muted-foreground">-</span>
                  <TimePickerCombobox
                    value={slot.end}
                    onChange={(value) => onUpdateTimeSlot(day, index, { ...slot, end: value })}
                    otherTimeSlots={settings.timeSlots.filter((_, i) => i !== index)}
                    isStartTime={false}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemoveTimeSlot(day, index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onAddTimeSlot(day)}
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>
                  {index === 0 && (
                    <CopyDaysPopup
                      isOpen={isCopyPopupOpen}
                      onClose={(open = false) => setIsCopyPopupOpen(open)}
                      onApply={(selectedDays) => {
                        onCopyDay(day, selectedDays)
                        setIsCopyPopupOpen(false)
                      }}
                      sourceDay={day}
                      allDays={DAYS}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}